import { type TypedDocumentNode, gql } from '@apollo/client';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { WatchlistMetas_watchlist } from './__generated__/index.graphql';

const Metas = styled(LabelS)`
  svg {
    margin-right: var(--unit);
  }
`;

const Separator = () => (
  <LabelS color="var(--c-nd-150)" as="span">
    {' • '}
  </LabelS>
);

type Props = {
  watchlist: WatchlistMetas_watchlist;
  withCreatedAt?: boolean;
};

export const WatchlistMetas = ({ watchlist, withCreatedAt = false }: Props) => {
  const { totalPlayersCount, createdAt, updatedAt } = watchlist;
  const { formatDistanceToNow, formatDate } = useIntlContext();

  const updatedAgo = formatDistanceToNow(updatedAt || createdAt);
  return (
    <Metas color="var(--c-nd-600)">
      <FontAwesomeIcon icon={faUserGroup} color="var(--c-nd-600)" />
      <FormattedMessage
        id="watchlistMetas.playersCount"
        defaultMessage="{totalPlayersCount, plural, one {# player} other {# players}}"
        values={{ totalPlayersCount }}
      />
      {!!withCreatedAt && (
        <>
          <Separator />
          <FormattedMessage
            {...glossary.created}
            values={{
              date: formatDate(createdAt),
            }}
          />
        </>
      )}
      <Separator />
      <FormattedMessage
        id="watchlistMetas.updated"
        defaultMessage="Updated {updatedAgo}"
        values={{ updatedAgo }}
      />
    </Metas>
  );
};

WatchlistMetas.fragments = {
  watchlist: gql`
    fragment WatchlistMetas_watchlist on Watchlist {
      id
      title
      totalPlayersCount
      createdAt
      updatedAt
    }
  ` as TypedDocumentNode<WatchlistMetas_watchlist>,
};
